import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { AssistantContext } from '../Contexts/index';
import { BuilderContext } from '../Contexts/index';
import { aiEditConversation } from '../api/index';

const AssistantProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  // conversation is read here so the propose API (PR 2) can receive
  // the workflow id and a snapshot of the current graph as context.
  const { conversation, dispatch } = React.useContext(BuilderContext);


  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handlePromptChange = useCallback(event => {
    setPrompt(event.target.value);
  }, []);

  const addUserMessage = useCallback(text => {
    setMessages(prev =>
      prev.concat({ id: Date.now(), role: 'user', content: text })
    );
  }, []);

  const addAssistantMessage = useCallback(text => {
    setMessages(prev =>
      prev.concat({ id: Date.now(), role: 'assistant', content: text })
    );
  }, []);

  const clearPrompt = useCallback(() => setPrompt(''), []);

  const handleSubmit = useCallback(
    text => {
      if (!text.trim() || loading) {
        return;
      }

      addUserMessage(text);
      clearPrompt();

      setLoading(true);

      aiEditConversation({ conversationId: conversation.id, prompt: text })
        .then(({ data }) => {
          addAssistantMessage(
            data.message ?? 'Workflow updated successfully.'
          );
          dispatch({ type: 'RESET_CONVERSATION', conversationDetails: data.data });
        })
        .catch(error => {
          const message =
            error?.response?.data?.message ??
            'Something went wrong. Please try again.';
          addAssistantMessage(message);
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, conversation, dispatch, addUserMessage, addAssistantMessage, clearPrompt]
  );

  const value = {
    open,
    messages,
    prompt,
    loading,
    handleOpen,
    handleClose,
    handlePromptChange,
    handleSubmit,
    addUserMessage,
    addAssistantMessage,
    clearPrompt
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

AssistantProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AssistantProvider;
